import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AppLogo } from "@/components/app/Primitives";
import { DemoGuide } from "@/components/app/DemoGuide";
import VersionBadge from "@/components/app/VersionBadge";
import { MobileNavProvider, useMobileNav } from "@/components/app/Shell";
import TID from "@/lib/testIds";
import api from "@/lib/api";
import {
  LogOut, ChevronDown, Menu, X, Bell,
  LayoutDashboard, FileText, Users, ShieldCheck, Package,
  Wallet, Inbox, Settings, ScrollText, Upload, Handshake, BarChart3,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/* Nav for each role */
const NAV = {
  hul_approver: [
    { key: "approvals", to: "/approver", end: true, label: "Payment approvals", icon: Wallet },
    { key: "campaigns", to: "/approver/campaigns", label: "Campaigns", icon: FileText },
    { key: "exceptions", to: "/approver/exceptions", label: "Exception inbox", icon: ShieldCheck },
    { key: "notifications", to: "/approver/notifications", label: "Inbox", icon: Inbox },
    { key: "tower", to: "/approver/tower", label: "Control Tower", icon: BarChart3 },
    { key: "integrations", to: "/approver/integrations", label: "Integrations", icon: Settings },
    { key: "readiness", to: "/approver/readiness", label: "Readiness report", icon: ScrollText },
    { key: "scenarios", to: "/approver/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
  customer: [
    { key: "dashboard", to: "/customer", end: true, label: "Dashboard", icon: LayoutDashboard },
    { key: "campaigns", to: "/customer/campaigns", label: "Campaigns & audit", icon: FileText },
    { key: "orders", to: "/customer/orders", label: "Orders", icon: FileText },
    { key: "exceptions", to: "/customer/exceptions", label: "Exception inbox", icon: ShieldCheck },
    { key: "memos", to: "/customer/payments-memos", label: "Payment memos", icon: Wallet },
    { key: "autofetch", to: "/customer/autofetch", label: "Auto Fetch monitor", icon: Inbox },
    { key: "reports", to: "/customer/reports", label: "Reports", icon: BarChart3 },
    { key: "rules", to: "/customer/rules", label: "Rules & authority", icon: ShieldCheck },
    { key: "status", to: "/customer/status-request", label: "Request status update", icon: Inbox },
    { key: "admin", to: "/customer/admin", label: "Branding & settings", icon: Settings },
    { key: "notifications", to: "/customer/notifications", label: "Inbox", icon: Inbox },
    { key: "tower", to: "/customer/tower", label: "Control Tower", icon: BarChart3 },
    { key: "integrations", to: "/customer/integrations", label: "Integrations", icon: Settings },
    { key: "readiness", to: "/customer/readiness", label: "Readiness report", icon: ScrollText },
    { key: "scenarios", to: "/customer/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
  procurement_desk: [
    { key: "dashboard", to: "/procurement", end: true, label: "Dashboard", icon: LayoutDashboard },
    { key: "campaigns", to: "/procurement/campaigns", label: "Campaigns (AdFluence)", icon: FileText },
    { key: "aggregate_pos", to: "/procurement/aggregate-pos", label: "Aggregate POs (Coupa)", icon: Package },
    { key: "orders", to: "/procurement/orders", label: "Order queue", icon: FileText },
    { key: "influencers", to: "/procurement/influencers", label: "Influencer master", icon: Users },
    { key: "kys", to: "/procurement/compliance", label: "KYS queue", icon: ShieldCheck },
    { key: "pos", to: "/procurement/pos", label: "Back-to-back POs", icon: Package },
    { key: "payments", to: "/procurement/payments", label: "Disbursements", icon: Wallet },
    { key: "exceptions", to: "/procurement/exceptions", label: "Exception inbox", icon: ShieldCheck },
    { key: "memos", to: "/procurement/payments-memos", label: "Payment memos", icon: Wallet },
    { key: "autofetch", to: "/procurement/autofetch", label: "Auto Fetch monitor", icon: Inbox },
    { key: "reports", to: "/procurement/reports", label: "Reports (W/M/Q)", icon: BarChart3 },
    { key: "notifications", to: "/procurement/notifications", label: "Inbox", icon: Inbox },
    { key: "tower", to: "/procurement/tower", label: "Control Tower", icon: BarChart3 },
    { key: "integrations", to: "/procurement/integrations", label: "Integrations", icon: Settings },
    { key: "readiness", to: "/procurement/readiness", label: "Readiness report", icon: ScrollText },
    { key: "scenarios", to: "/procurement/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
  ey_auditor: [
    { key: "dashboard", to: "/ey", end: true, label: "EY Dashboard", icon: LayoutDashboard },
    { key: "campaigns", to: "/ey/campaigns", label: "Campaigns", icon: FileText },
    { key: "deliverables", to: "/ey/deliverables", label: "Pending EY reviews", icon: ShieldCheck },
    { key: "exceptions", to: "/ey/exceptions", label: "Exception inbox", icon: ShieldCheck },
    { key: "autofetch", to: "/ey/autofetch", label: "Auto Fetch monitor", icon: Inbox },
    { key: "reports", to: "/ey/reports", label: "Reports (W/M/Q)", icon: BarChart3 },
    { key: "audit", to: "/ey/audit", label: "EY audit trail", icon: ScrollText },
    { key: "notifications", to: "/ey/notifications", label: "Inbox", icon: Inbox },
    { key: "tower", to: "/ey/tower", label: "Control Tower", icon: BarChart3 },
    { key: "integrations", to: "/ey/integrations", label: "Integrations", icon: Settings },
    { key: "readiness", to: "/ey/readiness", label: "Readiness report", icon: ScrollText },
    { key: "scenarios", to: "/ey/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
  influencer: [
    { key: "dashboard", to: "/influencer", end: true, label: "Dashboard", icon: LayoutDashboard },
    { key: "kys", to: "/influencer/kys", label: "KYS verification", icon: ShieldCheck },
    { key: "pos", to: "/influencer/pos", label: "My POs", icon: Handshake },
    { key: "deliverables", to: "/influencer/deliverables", label: "Deliverables", icon: Upload },
    { key: "payments", to: "/influencer/payments", label: "Payments", icon: Wallet },
    { key: "invoices", to: "/influencer/invoices", label: "Invoices", icon: FileText },
    { key: "notifications", to: "/influencer/notifications", label: "Inbox", icon: Inbox },
    { key: "scenarios", to: "/influencer/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
  propay_admin: [
    { key: "dashboard", to: "/admin", end: true, label: "L3 Dashboard", icon: LayoutDashboard },
    { key: "orders", to: "/admin/orders", label: "All orders", icon: FileText },
    { key: "influencers", to: "/admin/influencers", label: "All influencers", icon: Users },
    { key: "kys", to: "/admin/compliance", label: "KYS queue", icon: ShieldCheck },
    { key: "pos", to: "/admin/pos", label: "All POs", icon: Package },
    { key: "deliverables", to: "/admin/deliverables", label: "All deliverables", icon: Upload },
    { key: "payments", to: "/admin/payments", label: "All payments", icon: Wallet },
    { key: "campaigns", to: "/admin/campaigns", label: "Campaigns", icon: FileText },
    { key: "exceptions", to: "/admin/exceptions", label: "Exception inbox", icon: ShieldCheck },
    { key: "memos", to: "/admin/payments-memos", label: "Payment memos", icon: Wallet },
    { key: "rules", to: "/admin/rules", label: "Rules & authority", icon: ShieldCheck },
    { key: "customers", to: "/admin/customers", label: "Customers & branding", icon: Settings },
    { key: "audit", to: "/admin/audit", label: "Audit log", icon: ScrollText },
    { key: "notifications", to: "/admin/notifications", label: "Inbox", icon: Inbox },
    { key: "tower", to: "/admin/tower", label: "Control Tower", icon: BarChart3 },
    { key: "integrations", to: "/admin/integrations", label: "Integrations", icon: Settings },
    { key: "readiness", to: "/admin/readiness", label: "Readiness report", icon: ScrollText },
    { key: "scenarios", to: "/admin/scenarios", label: "Usage scenarios", icon: ScrollText },
  ],
};

const ROLE_LABEL = {
  customer: "HUL Media Finance",
  procurement_desk: "ProPay Operations",
  influencer: "Creator / Agency",
  propay_admin: "Administrator · L3",
  ey_auditor: "Auditor (EY)",
  hul_approver: "HUL Approver",
};

function Sidebar({ items, tenantLabel }) {
  const { open, setOpen } = useMobileNav();
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside
        data-testid={TID.sidebar}
        className={`fixed lg:sticky top-0 left-0 z-50 lg:z-auto w-72 lg:w-64 h-screen bg-white border-r border-slate-200 flex flex-col flex-shrink-0 transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <AppLogo />
          <button onClick={() => setOpen(false)} className="lg:hidden p-1.5 rounded-md hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100 text-[11px] uppercase tracking-widest text-slate-400 font-semibold truncate">{tenantLabel}</div>
        <nav className="flex-1 overflow-y-auto p-3 thin-scroll">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              data-testid={TID.sidebarLink(it.key)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 my-0.5 text-sm rounded-md transition-colors ${isActive ? "bg-[#0052FF] text-white" : "text-slate-700 hover:bg-slate-100"}`
              }
            >
              {it.icon && <it.icon size={16} />}
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t text-[11px] text-slate-400 space-y-1">
          <div>Issued by <span className="font-semibold text-slate-600">PROPAY GLOBAL PRIVATE LIMITED</span></div>
          <VersionBadge />
        </div>
      </aside>
    </>
  );
}

function NotificationsBell() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications?unread_only=true");
        setCount(data.length);
      } catch (err) {
        // Transient failures (network, 401 during logout) are non-fatal —
        // the bell simply keeps the last known count.
        console.debug("notification poll failed:", err?.message);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid={TID.topbarNotifications} className="relative inline-flex items-center px-2">
      <Bell size={18} className="text-slate-600" />
      {count > 0 && (
        <span className="absolute -top-1 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </div>
  );
}

function TenantBrandBadge() {
  // Renders the FMCG customer's own logo + name on the top-right — creates a
  // co-branded feel while ProPay's identity stays on the left sidebar.
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (user?.role !== "customer" || !user?.tenant_id) return;
    let cancelled = false;
    api.get(`/tenants/${user.tenant_id}`).then((r) => {
      if (!cancelled) setTenant(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.role, user?.tenant_id]);

  if (user?.role !== "customer" || !tenant) return null;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const logoSrc = tenant.logo_url
    ? (tenant.logo_url.startsWith("http") ? tenant.logo_url : `${BACKEND}${tenant.logo_url}`)
    : null;
  return (
    <div
      data-testid="tenant-brand-badge"
      className="hidden md:flex items-center gap-2 pl-3 pr-2 py-1 rounded-lg border"
      style={{ borderColor: (tenant.brand_color || "#e2e8f0") + "40",
               background: (tenant.brand_color || "#f8fafc") + "0d" }}
    >
      {logoSrc ? (
        <img src={logoSrc} alt={tenant.name} className="h-6 max-w-[80px] object-contain" />
      ) : (
        <div className="w-6 h-6 rounded grid place-items-center text-[10px] font-bold text-white"
             style={{ background: tenant.brand_color || "#0052FF" }}>
          {(tenant.name || "?").slice(0, 1)}
        </div>
      )}
      <span className="text-xs font-semibold text-slate-700 tracking-wide">{tenant.name}</span>
    </div>
  );
}

function Topbar({ title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { setOpen } = useMobileNav();
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
      <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
        <button onClick={() => setOpen(true)} className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"><Menu size={20} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-lg sm:text-xl font-bold text-slate-900 truncate">{title || ROLE_LABEL[user?.role]}</h1>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        <TenantBrandBadge />
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger data-testid={TID.topbarUser} className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-md hover:bg-slate-100 text-sm">
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center font-heading font-bold text-xs">
              {(user?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
            <div className="hidden md:block text-left">
              <div className="font-semibold text-slate-800 leading-tight">{user?.name}</div>
              <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role]}</div>
            </div>
            <ChevronDown size={14} className="text-slate-400 hidden md:block" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="text-xs text-slate-400 mt-0.5 truncate">{user?.tenant_name}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/guide")}>Platform guide</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={TID.topbarLogout} onClick={logout} className="text-rose-600">
              <LogOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const items = NAV[user?.role] || [];
  return (
    <MobileNavProvider>
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar items={items} tenantLabel={user?.tenant_name} />
        <div className="flex-1 min-w-0 flex flex-col">
          <Topbar />
          <main className="flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          <DemoGuide />
          </main>
        </div>
      </div>
    </MobileNavProvider>
  );
}
